// ═══════════════════════════════════════════════════════════════
// MainStreet — Feed & Inbox
// Broadcast posts + targeted messages with replies
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, Textarea,
  Select, Modal, ActionIcon, Stack, MultiSelect,
  SegmentedControl, Collapse,
} from '../../components/gab/GabComponents';
import { useDisclosure } from '../../components/gab/GabHooks';
import { notifications } from '../../lib/notifications';
import {
  IconPlus, IconRefresh, IconTrash, IconSend, IconInbox,
  IconSpeakerphone, IconCornerDownRight, IconChevronDown, IconChevronRight,
} from '@tabler/icons-react';
import { useMainStreetOpsStore } from '../../store/mainstreetOpsStore';
import { useMainStreetStore } from '../../store/mainstreetStore';
import type { MSPost } from '../../store/mainstreetOpsStore';
import { useAuthStore } from '../../store/authStore';
import { fmtDateTime } from '../../lib/dateFormat';

const POST_TYPE_OPTIONS = [
  { value: 'announcement', label: 'Announcement' },
  { value: 'message', label: 'Direct Message' },
  { value: 'question', label: 'Question' },
  { value: 'update', label: 'Update' },
  { value: 'alert', label: 'Alert' },
];

const PRIORITY_OPTIONS = [
  { value: 'normal', label: 'Normal' },
  { value: 'urgent', label: 'Urgent' },
  { value: 'critical', label: 'Critical' },
];

const TYPE_COLORS: Record<string, string> = {
  announcement: 'blue', message: 'violet', question: 'grape',
  update: 'cyan', alert: 'red',
};

const PRIORITY_COLORS: Record<string, string> = {
  normal: 'gray', urgent: 'orange', critical: 'red',
};

const BORDER_COLORS: Record<string, string> = {
  urgent: 'var(--mantine-color-orange-5)',
  critical: 'var(--mantine-color-red-5)',
};

export default function Feed() {
  const {
    posts, loading,
    loadPosts, createPost, deletePost,
  } = useMainStreetOpsStore();
  const { organizations, loadOrganizations, orgLists, loadOrgLists } = useMainStreetStore();
  const { activeIncident, user } = useAuthStore();

  // Create modal
  const [createOpened, { open: openCreate, close: closeCreate }] = useDisclosure(false);
  const [content, setContent] = useState('');
  const [postType, setPostType] = useState('update');
  const [priority, setPriority] = useState('normal');
  const [targetType, setTargetType] = useState('broadcast');
  const [targetOrgIds, setTargetOrgIds] = useState<string[]>([]);
  const [targetListId, setTargetListId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // View mode
  const [viewMode, setViewMode] = useState('all');
  const [filterType, setFilterType] = useState('all');

  // Reply
  const [replyTo, setReplyTo] = useState<MSPost | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [replyingSaving, setReplyingSaving] = useState(false);

  // Expanded threads
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());

  // Delete
  const [deleteTarget, setDeleteTarget] = useState<MSPost | null>(null);
  const [deleteOpened, { open: openDelete, close: closeDelete }] = useDisclosure(false);

  useEffect(() => { loadPosts(); loadOrganizations(); loadOrgLists(); }, []);

  const orgOptions = useMemo(() =>
    organizations.map(o => ({ value: String(o.id || ''), label: String(o.name || `Org #${o.id}`) })),
  [organizations]);

  const listOptions = useMemo(() =>
    orgLists.map(l => ({ value: String(l.id), label: `${l.name} (${(l.orgIds || '').split(',').filter(Boolean).length})` })),
  [orgLists]);

  // Separate top-level posts from replies
  const topLevelPosts = useMemo(() =>
    posts.filter(p => !p.parentPostId),
  [posts]);

  const repliesByParent = useMemo(() => {
    const map: Record<string, MSPost[]> = {};
    posts.filter(p => p.parentPostId).forEach(p => {
      if (!map[p.parentPostId]) map[p.parentPostId] = [];
      map[p.parentPostId].push(p);
    });
    // Sort replies by date
    Object.values(map).forEach(arr => arr.sort((a, b) => (a.createdAt ?? '').localeCompare(b.createdAt ?? '')));
    return map;
  }, [posts]);

  const filtered = useMemo(() => {
    let list = topLevelPosts; // V2 incidentId filtering handled at load time
    if (viewMode === 'targeted') {
      list = list.filter(p => p.targetType && p.targetType !== 'broadcast');
    } else if (viewMode === 'broadcast') {
      list = list.filter(p => !p.targetType || p.targetType === 'broadcast');
    }
    if (filterType !== 'all') list = list.filter(p => p.postType === filterType);
    return [...list].sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''));
  }, [topLevelPosts, viewMode, filterType]);

  // Resolve target display
  const getTargetLabel = (post: MSPost): string => {
    if (!post.targetType || post.targetType === 'broadcast') return 'All';
    if (post.targetListId) {
      const list = orgLists.find(l => String(l.id) === post.targetListId);
      return list ? `List: ${list.name}` : 'List';
    }
    if (post.targetOrgIds) {
      const ids = (post.targetOrgIds || '').split(',').filter(Boolean);
      if (ids.length <= 2) {
        return ids.map(id => organizations.find(o => String(o.id) === id)?.name || `Org #${id}`).join(', ');
      }
      return `${ids.length} organizations`;
    }
    return 'All';
  };

  // ── Create ─────────────────────────────────────────────────

  async function handleCreate() {
    if (!content.trim()) {
      notifications.show({ color: 'red', title: 'Required', message: 'Content is required.' });
      return;
    }
    setSaving(true);

    // Resolve list to org IDs
    let resolvedOrgIds = targetOrgIds.join(',');
    if (targetType === 'list' && targetListId) {
      const list = orgLists.find(l => String(l.id) === targetListId);
      if (list) resolvedOrgIds = list.orgIds;
    }

    await createPost({
      incidentId: activeIncident ? String(activeIncident.id) : '',
      content: content.trim(),
      postType,
      priority,
      authorName: user?.fullName ?? user?.email ?? 'Unknown',
      createdAt: new Date().toISOString(),
      targetType: targetType === 'broadcast' ? 'broadcast' : targetType,
      targetOrgIds: targetType !== 'broadcast' ? resolvedOrgIds : '',
      targetListId: targetType === 'list' ? (targetListId ?? '') : '',
    });
    setSaving(false);
    notifications.show({ color: 'green', title: 'Sent', message: targetType === 'broadcast' ? 'Post published.' : 'Message sent.' });
    setContent(''); setPostType('update'); setPriority('normal');
    setTargetType('broadcast'); setTargetOrgIds([]); setTargetListId(null);
    closeCreate();
  }

  // ── Reply ──────────────────────────────────────────────────

  async function handleReply() {
    if (!replyTo || !replyContent.trim()) return;
    setReplyingSaving(true);
    await createPost({
      incidentId: activeIncident ? String(activeIncident.id) : '',
      content: replyContent.trim(),
      postType: 'update',
      priority: 'normal',
      authorName: user?.fullName ?? user?.email ?? 'Unknown',
      createdAt: new Date().toISOString(),
      parentPostId: String(replyTo.id),
      targetType: replyTo.targetType || '',
      targetOrgIds: replyTo.targetOrgIds || '',
    });
    setReplyingSaving(false);
    notifications.show({ color: 'green', message: 'Reply posted.' });
    setReplyContent('');
    setReplyTo(null);
    // Expand thread
    setExpandedIds(prev => new Set(prev).add(replyTo.id));
  }

  // ── Delete ─────────────────────────────────────────────────

  async function handleDelete() {
    if (!deleteTarget) return;
    await deletePost(deleteTarget.id);
    closeDelete();
    setDeleteTarget(null);
    notifications.show({ color: 'green', message: 'Post removed.' });
  }

  const toggleExpand = (id: number) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // ── Render ─────────────────────────────────────────────────

  const typeFilterOptions = [
    { value: 'all', label: 'All Types' },
    ...POST_TYPE_OPTIONS,
  ];

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Feed & Inbox</Title>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
            loading={loading} onClick={() => loadPosts()}>Refresh</Button>
          <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>
            New Post
          </Button>
        </Group>
      </Group>

      <Group mb="md" gap="sm">
        <SegmentedControl size="xs" data={[
          { value: 'all', label: 'All' },
          { value: 'broadcast', label: 'Broadcasts' },
          { value: 'targeted', label: 'Targeted' },
        ]} value={viewMode} onChange={setViewMode} />
        <Select data={typeFilterOptions} value={filterType}
          onChange={v => setFilterType(v ?? 'all')} size="xs" w={160} />
      </Group>

      <Text size="xs" c="dimmed" mb="md">{filtered.length} post{filtered.length !== 1 ? 's' : ''}</Text>

      {filtered.length === 0 && (
        <Paper p="xl" withBorder>
          <Text ta="center" c="dimmed">{loading ? 'Loading...' : 'No posts found.'}</Text>
        </Paper>
      )}

      {filtered.map(post => {
        const borderColor = BORDER_COLORS[post.priority];
        const isTargeted = post.targetType && post.targetType !== 'broadcast';
        const replies = repliesByParent[String(post.id)] || [];
        const isExpanded = expandedIds.has(post.id);

        return (
          <Paper key={post.id} p="sm" mb="sm" withBorder
            style={{
              ...(borderColor ? { borderLeft: `4px solid ${borderColor}` } : {}),
              ...(isTargeted ? { background: 'var(--mantine-color-violet-0)' } : {}),
            }}>
            <Group justify="space-between" wrap="nowrap" mb={4}>
              <Group gap="xs">
                {isTargeted ? <IconInbox size={14} color="#7c3aed" /> : <IconSpeakerphone size={14} color="#888" />}
                <Text size="sm" fw={600}>{post.authorName}</Text>
                <Badge size="xs" color={TYPE_COLORS[post.postType] ?? 'gray'}>{post.postType}</Badge>
                {post.priority !== 'normal' && (
                  <Badge size="xs" color={PRIORITY_COLORS[post.priority] ?? 'gray'}>{post.priority}</Badge>
                )}
                {isTargeted && (
                  <Badge size="xs" variant="light" color="violet">To: {getTargetLabel(post)}</Badge>
                )}
              </Group>
              <Group gap={4}>
                <Text size="xs" c="dimmed">{fmtDateTime(post.createdAt)}</Text>
                <ActionIcon size="xs" variant="subtle" color="gray"
                  onClick={() => { setReplyTo(post); setReplyContent(''); }}>
                  <IconCornerDownRight size={12} />
                </ActionIcon>
                <ActionIcon size="xs" variant="subtle" color="red"
                  onClick={() => { setDeleteTarget(post); openDelete(); }}>
                  <IconTrash size={12} />
                </ActionIcon>
              </Group>
            </Group>
            <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{post.content}</Text>

            {/* Replies */}
            {replies.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <Button variant="subtle" size="compact-xs" color="gray"
                  leftSection={isExpanded ? <IconChevronDown size={12} /> : <IconChevronRight size={12} />}
                  onClick={() => toggleExpand(post.id)}>
                  {replies.length} {replies.length === 1 ? 'reply' : 'replies'}
                </Button>
                <Collapse in={isExpanded}>
                  <Stack gap={4} mt={4} ml={16} style={{ borderLeft: '2px solid #e5e7eb', paddingLeft: 12 }}>
                    {replies.map(reply => (
                      <div key={reply.id}>
                        <Group gap="xs" mb={2}>
                          <Text size="xs" fw={600}>{reply.authorName}</Text>
                          <Text size="xs" c="dimmed">{fmtDateTime(reply.createdAt)}</Text>
                        </Group>
                        <Text size="xs" style={{ whiteSpace: 'pre-wrap' }}>{reply.content}</Text>
                      </div>
                    ))}
                  </Stack>
                </Collapse>
              </div>
            )}

            {/* Inline reply */}
            {replyTo?.id === post.id && (
              <Paper p="xs" mt="sm" withBorder style={{ background: 'var(--mantine-color-gray-0)' }}>
                <Textarea size="xs" placeholder="Write a reply..." value={replyContent}
                  onChange={e => setReplyContent(e.target.value)}
                  autosize minRows={2} mb="xs" />
                <Group justify="flex-end" gap={4}>
                  <Button size="compact-xs" variant="subtle" onClick={() => setReplyTo(null)}>Cancel</Button>
                  <Button size="compact-xs" loading={replyingSaving} onClick={handleReply}
                    leftSection={<IconSend size={10} />}>Reply</Button>
                </Group>
              </Paper>
            )}
          </Paper>
        );
      })}

      {/* ── Create Modal ────────────────────────────────────── */}
      <Modal opened={createOpened} onClose={closeCreate} title="New Post / Message" size="md">
        <SegmentedControl fullWidth size="xs" mb="sm" data={[
          { value: 'broadcast', label: 'Broadcast to All' },
          { value: 'list', label: 'Send to List' },
          { value: 'specific', label: 'Send to Specific' },
        ]} value={targetType} onChange={setTargetType} />

        {targetType === 'list' && (
          <Select label="Target List" placeholder="Select a list..."
            data={listOptions} value={targetListId} onChange={setTargetListId}
            size="sm" mb="sm" />
        )}
        {targetType === 'specific' && (
          <MultiSelect label="Target Organizations" data={orgOptions}
            value={targetOrgIds} onChange={setTargetOrgIds}
            placeholder="Select organizations" size="sm" mb="sm" searchable />
        )}

        <Textarea label="Content" value={content}
          onChange={e => setContent(e.target.value)}
          placeholder="Write your message..." size="sm" mb="sm" autosize minRows={4} required />

        <Group gap="sm" mb="md">
          <Select label="Type" data={POST_TYPE_OPTIONS} value={postType}
            onChange={v => setPostType(v ?? 'update')} size="sm" style={{ flex: 1 }} />
          <Select label="Priority" data={PRIORITY_OPTIONS} value={priority}
            onChange={v => setPriority(v ?? 'normal')} size="sm" style={{ flex: 1 }} />
        </Group>

        <Group justify="flex-end">
          <Button variant="subtle" onClick={closeCreate}>Cancel</Button>
          <Button loading={saving} onClick={handleCreate}
            leftSection={<IconSend size={16} />}>
            {targetType === 'broadcast' ? 'Publish' : 'Send'}
          </Button>
        </Group>
      </Modal>

      {/* ── Delete Confirmation ─────────────────────────────── */}
      <Modal opened={deleteOpened} onClose={closeDelete} title="Delete Post" size="sm">
        <Text size="sm" mb="md">Delete this post? This cannot be undone.</Text>
        <Group justify="flex-end">
          <Button variant="subtle" size="xs" onClick={closeDelete}>Cancel</Button>
          <Button color="red" size="xs" onClick={handleDelete}>Delete</Button>
        </Group>
      </Modal>
    </div>
  );
}
